import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Swal from "sweetalert2";
import {Button} from "../../components/Button/index";
import {AuthContext} from "../../contexts/Auth/AuthContext";
import {useTheme} from "../../contexts/Theme/ThemeContext";
import {ViewLogin} from "./styles";
import {Input} from "../../components/Input";
import Form from "../../components/Form";
import {AssinaturaApi} from "../../hooks/assinaturaApi";
import ModalEsqueceuSenha from "../../components/Modal/ModalEsqueceuSenha";

interface SessaoAtivaOutroDispositivoData {
    error: string;
    message: string;
}

interface DoisFatoresRequeridoData {
    error: string;
    desafio: string;
    message: string;
}

const Login: React.FC = (props) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [formValid, setFormValid] = useState(false);
    const [modalEsqueceuSenha, setModalEsqueceuSenha] = useState(false);

    const [desafio, setDesafio] = useState("");
    const [codigo, setCodigo] = useState("");
    const [verificando, setVerificando] = useState(false);

    const auth = useContext(AuthContext);
    const navigate = useNavigate();
    const {theme} = useTheme();

    // Fluxo novo em modo restrito vai direto pra /assinatura. Senão, home.
    const irParaDestinoPosLogin = async () => {
        try {
            const info = await AssinaturaApi().getInfoAssinatura();
            if (info.fluxo_novo && info.modo_restrito) {
                navigate("/assinatura");
                return;
            }
        } catch (e) {
            // Erro silencioso — fallback pra home
        }
        navigate("/");
    };

    const concluirLogin = async () => {
        await auth.validateToken();
        localStorage.removeItem("produtosSettings");
        await irParaDestinoPosLogin();
    };

    const voltarParaSenha = () => {
        setDesafio("");
        setCodigo("");
        setPassword("");
    };

    const mensagemErroLogin = (data: any) => {
        switch (data) {
            case "VERSAO_ANTIGA":
                return "Empresa na versão antiga!";
            case "LOGIN_FALHOU":
                return "Usuário ou senha inválidos!";
            case "TOO_MANY_REQUESTS":
                return "Muitas tentativas de login, tente novamente em alguns minutos!";
            default:
                return "Erro inesperado ao fazer login!";
        }
    };

    const tratarRespostaLogin = async (data: any, jaForcouLogin: boolean) => {
        if (data && data === "LOGADO") {
            await concluirLogin();
            return;
        }

        const doisFatores = typeof data === "object" && (data as DoisFatoresRequeridoData).error === "2FA_REQUERIDO";

        if (doisFatores) {
            setDesafio((data as DoisFatoresRequeridoData).desafio);
            setCodigo("");
            return;
        }

        const isSessaoAtivaOutroDispositivo = typeof data === "object" && (data as SessaoAtivaOutroDispositivoData).error === "SESSAO_ATIVA_OUTRO_DISPOSITIVO";

        if (isSessaoAtivaOutroDispositivo && !jaForcouLogin) {
            Swal.fire({
                icon: "warning",
                title: "Este usuário já está logado em outro dispositivo",
                html: `Ao efetuar o login, as demais sessões deste usuário serão fechadas e os dados não salvos serão perdidos. Para acessar o Conecta Venda em mais dispositivos, faça upgrade para um plano com mais usuários.<br><br><a href="https://api.whatsapp.com/send?phone=555434434563&text=Ol%C3%A1%2C%20gostaria%20de%20fazer%20upgrade%20do%20meu%20plano%20para%20mais%20usu%C3%A1rios." target="_blank" style="color: ${theme.colors.primary};">Clique aqui para fazer upgrade</a>`,
                showCancelButton: true,
                confirmButtonText: "Login",
                cancelButtonText: "Cancelar",
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const dataForced = await auth.signin(email, password, true);
                    await tratarRespostaLogin(dataForced, true);
                }
            });
            return;
        }

        Swal.fire({
            icon: "error",
            title: "Oops...",
            text: mensagemErroLogin(data),
        });
    };

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (email && password) {
            const data = await auth.signin(email, password);
            await tratarRespostaLogin(data, false);
        }
    };

    const handleCodigo = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!desafio || !codigo) return;

        setVerificando(true);
        const data = await auth.verificar2FA(desafio, codigo);
        setVerificando(false);

        if (data === "LOGADO") {
            await concluirLogin();
            return;
        }

        const precisaRecomecar = data === "2FA_DESAFIO_INVALIDO" || data === "2FA_TENTATIVAS_EXCEDIDAS";

        const text = (() => {
            switch (data) {
                case "2FA_CODIGO_INVALIDO":
                    return "Código inválido. Confira o aplicativo e tente de novo.";
                case "2FA_TENTATIVAS_EXCEDIDAS":
                    return "Muitas tentativas com código errado. Faça o login novamente.";
                case "2FA_DESAFIO_INVALIDO":
                    return "O tempo para informar o código acabou. Faça o login novamente.";
                case "TOO_MANY_REQUESTS":
                    return "Muitas tentativas, tente novamente em alguns minutos!";
                default:
                    return "Erro inesperado ao validar o código!";
            }
        })();

        Swal.fire({icon: "error", title: "Oops...", text}).then(() => {
            if (precisaRecomecar) {
                voltarParaSenha();
            } else {
                setCodigo("");
            }
        });
    };

    useEffect(() => {
        setTimeout(() => {
            if (Boolean(localStorage.getItem("caiu_sessao")) && !Swal.isVisible()) {
                Swal.fire({
                    title: "Sua sessão expirou!",
                    text: "Por favor, faça login novamente.",
                    icon: "error",
                    confirmButtonText: "Ok",
                }).then(() => {
                    localStorage.removeItem("caiu_sessao");
                });
            }
        }, 1500);
    }, []);

    return (
        <ViewLogin>
            <div className="login">
                <div className="topo-login">
                    <div className="logo">
                        <img
                            src={require(`../../assets/images/logo-conecta-venda-horizontal.svg`).default}
                            className="img-responsive logo-svg"
                            title="Logo login"
                        ></img>
                    </div>
                    <div className="icone-canto">
                        <div className="icone">
                            <img
                                src={require(`../../assets/images/icons/icone-rocket.svg`).default}
                                className="img-responsive icone-svg"
                                title="Logo login"
                            ></img>
                        </div>
                    </div>
                </div>
                <div className="bg-login">
                    <div className="texto">
                        Seu acesso para<br></br>vender mais online<br></br>começa
                        aqui!
                    </div>
                    <span className="linha"></span>
                    <div className="frase fonteTexto">
                        {desafio
                            ? "Digite o código de verificação do Google Authenticator."
                            : "Acesse sua conta, adicionando seus dados"}
                    </div>
                    <Form
                        id={"login-form"}
                        onSubmit={desafio ? handleCodigo : handleLogin}
                        setFormValid={setFormValid}
                    >
                        {desafio ? (
                            <div className="form-group">
                                <Input
                                    type="text"
                                    placeholder="000000"
                                    value={codigo}
                                    minLength={6}
                                    maxLength={6}
                                    onChange={(newValue => {
                                        setCodigo(newValue.replace(/\D/g, ""));
                                    })}
                                    required
                                />
                            </div>
                        ) : (
                            <>
                                <div className="form-group">
                                    <Input
                                        type="text"
                                        placeholder="E-mail/Usuário"
                                        value={email}
                                        onChange={(newValue => {
                                            setEmail(newValue);
                                        })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <Input
                                        type="password"
                                        placeholder="******"
                                        value={password}
                                        onChange={(newValue => {
                                            setPassword(newValue);
                                        })}
                                        required
                                        allowEye={true}
                                    />
                                </div>
                            </>
                        )}
                        <div className="form-group">
                            <div
                                className="esqueciSenha fonteRotulos"
                                onClick={() => {
                                    if (desafio) {
                                        voltarParaSenha();
                                    } else {
                                        setModalEsqueceuSenha(true);
                                    }
                                }}>
                                {desafio ? "Voltar" : "Esqueceu sua senha?"}
                            </div>
                        </div>
                        <div className="form-group">
                            <Button
                                className={`login-button ${formValid ? "" : "disabled"}`}
                                type="submit"
                                title="Entrar"
                                loading={verificando}
                            ></Button>
                        </div>
                    </Form>
                </div>
            </div>
            <ModalEsqueceuSenha
                isOpen={modalEsqueceuSenha}
                setModal={setModalEsqueceuSenha}
            />
        </ViewLogin>
    );
};

export default Login;
